import { isDemoMode } from '../config/env';
import { api } from './api';
import { demoAuth } from '../demo/demoApi';
import type {
  AuthSession,
  ListPeopleOpts,
  OtpPurpose,
  OtpRequestResult,
  PeoplePageResult,
  SignInPayload,
  SignUpPayload,
  User,
  UserRole,
} from '../types/auth';
import type { CoachProfile, PersonDetail, StudentProfile } from '../types/identity';

interface SignInResponse {
  accessToken: string;
  refreshToken?: string;
}

export const authService = {
  async signIn(payload: SignInPayload): Promise<AuthSession> {
    if (isDemoMode) {
      const session = demoAuth.signIn(payload.email);
      return session;
    }
    const data = await api.post<SignInResponse>('/signin', payload);
    localStorage.setItem('accessToken', data.accessToken);
    if (data.refreshToken) localStorage.setItem('refreshToken', data.refreshToken);
    return { accessToken: data.accessToken, refreshToken: data.refreshToken };
  },

  async signUp(payload: SignUpPayload): Promise<void> {
    if (isDemoMode) {
      demoAuth.signUp(payload);
      return;
    }
    await api.post('/signup', payload);
  },

  async requestOtp(phone: string, purpose: OtpPurpose): Promise<OtpRequestResult> {
    if (isDemoMode) return demoAuth.requestOtp(phone, purpose);
    return api.post<OtpRequestResult>('/auth/otp/request', { phone, purpose });
  },

  async verifyOtp(payload: {
    phone: string;
    code: string;
    purpose: OtpPurpose;
    name?: string;
    honorific?: 'mr' | 'ms';
    dateOfBirth?: number;
  }): Promise<AuthSession> {
    if (isDemoMode) return demoAuth.verifyOtp(payload);
    const data = await api.post<SignInResponse & { user?: User }>('/auth/otp/verify', payload);
    localStorage.setItem('accessToken', data.accessToken);
    if (data.refreshToken) localStorage.setItem('refreshToken', data.refreshToken);
    return { accessToken: data.accessToken, refreshToken: data.refreshToken };
  },

  async forgotPassword(email: string): Promise<{ message: string }> {
    if (isDemoMode) return demoAuth.forgotPassword(email);
    return api.post<{ message: string }>('/auth/forgot-password', { email });
  },

  async resetPasswordWithToken(token: string, password: string, passwordConfirmation?: string): Promise<{ message: string }> {
    if (isDemoMode) return demoAuth.resetPasswordWithToken(token, password);
    return api.post<{ message: string }>(`/auth/reset-password/${token}`, {
      password,
      passwordConfirmation: passwordConfirmation || password,
    });
  },

  async resetPasswordWithOtp(phone: string, code: string, password: string): Promise<{ message: string }> {
    if (isDemoMode) return demoAuth.resetPasswordWithOtp(phone, code, password);
    return api.post<{ message: string }>('/auth/reset-password/otp', { phone, code, password });
  },

  async getMe(): Promise<User> {
    if (isDemoMode) return demoAuth.getMe();
    return api.get<User>('/me');
  },

  async switchRole(role: UserRole): Promise<User> {
    if (isDemoMode) return demoAuth.switchRole(role);
    const res = await api.post<{ user: User; accessToken?: string }>('/auth/switch-role', { role });
    if (res.accessToken) {
      localStorage.setItem('accessToken', res.accessToken);
    }
    return res.user;
  },

  async listStudents(): Promise<User[]> {
    if (isDemoMode) return demoAuth.listStudents();
    return api.get<User[]>('/users?role=student');
  },

  async listCoaches(): Promise<User[]> {
    if (isDemoMode) return demoAuth.listCoaches();
    return api.get<User[]>('/users?role=coach');
  },

  async listPeople(role?: UserRole, opts?: ListPeopleOpts): Promise<User[]> {
    if (isDemoMode) return demoAuth.listPeople(role, opts);
    const params = new URLSearchParams();
    if (role) params.set('role', role);
    if (opts?.q) params.set('q', opts.q);
    if (opts?.limit) params.set('limit', String(opts.limit));
    if (opts?.status) params.set('status', opts.status);
    if (opts?.danceLevel && opts.danceLevel !== 'all') params.set('danceLevel', opts.danceLevel);
    if (opts?.enrollment && opts.enrollment !== 'all') params.set('enrollment', opts.enrollment);
    if (opts?.classId) params.set('classId', opts.classId);
    const qs = params.toString();
    return api.get<User[]>(qs ? `/users?${qs}` : '/users');
  },

  async listPeoplePage(role: UserRole, opts: ListPeopleOpts & { page: number }): Promise<PeoplePageResult> {
    if (isDemoMode) return demoAuth.listPeoplePage(role, opts);
    const params = new URLSearchParams();
    params.set('role', role);
    if (opts.q) params.set('q', opts.q);
    if (opts.status) params.set('status', opts.status);
    if (opts.danceLevel && opts.danceLevel !== 'all') params.set('danceLevel', opts.danceLevel);
    if (opts.enrollment && opts.enrollment !== 'all') params.set('enrollment', opts.enrollment);
    if (opts.classId) params.set('classId', opts.classId);
    params.set('page', String(opts.page));
    params.set('pageSize', String(opts.pageSize ?? 24));
    return api.get<PeoplePageResult>(`/users?${params.toString()}`);
  },

  async getPerson(userId: string): Promise<PersonDetail> {
    if (isDemoMode) return demoAuth.getPerson(userId);
    return api.get<PersonDetail>(`/users/${userId}`);
  },

  async getMyProfile(): Promise<PersonDetail> {
    if (isDemoMode) return demoAuth.getPerson(demoAuth.getMe()._id);
    return api.get<PersonDetail>('/me/profile');
  },

  async requestEmailVerification(): Promise<{ email: string; ttlSeconds: number; resendSeconds: number; debugCode?: string }> {
    if (isDemoMode) return demoAuth.requestEmailVerification();
    return api.post('/auth/contact/email/request', {});
  },

  async verifyEmailContact(code: string): Promise<User> {
    if (isDemoMode) return demoAuth.verifyEmailContact(code);
    return api.post<User>('/auth/contact/email/verify', { code });
  },

  async requestPhoneContactVerification(): Promise<{ phone: string; ttlSeconds: number; resendSeconds: number; debugCode?: string }> {
    if (isDemoMode) return demoAuth.requestPhoneContactVerification();
    return api.post('/auth/contact/phone/request', {});
  },

  async verifyPhoneContact(code: string): Promise<User> {
    if (isDemoMode) return demoAuth.verifyPhoneContact(code);
    return api.post<User>('/auth/contact/phone/verify', { code });
  },

  async createPerson(payload: {
    name: string;
    email: string;
    password: string;
    role: UserRole;
    phone?: string;
    honorific?: User['honorific'];
    contactVerified?: boolean;
  }): Promise<User> {
    if (isDemoMode) return demoAuth.createPerson(payload);
    return api.post<User>('/users', payload);
  },

  async updatePerson(
    userId: string,
    payload: Partial<Pick<User, 'name' | 'phone' | 'role' | 'status' | 'honorific' | 'packIcon'>>,
  ): Promise<User> {
    if (isDemoMode) return demoAuth.updatePerson(userId, payload);
    return api.patch<User>(`/users/${userId}`, payload);
  },

  async deletePerson(userId: string): Promise<{ deleted: boolean; userId: string }> {
    if (isDemoMode) return demoAuth.deletePerson(userId);
    return api.delete<{ deleted: boolean; userId: string }>(`/users/${userId}`);
  },

  async updateMe(payload: { name?: string; email?: string; phone?: string; honorific?: User['honorific']; packIcon?: User['packIcon'] }): Promise<User> {
    if (isDemoMode) return demoAuth.updatePerson(demoAuth.getMe()._id, payload);
    return api.patch<User>('/me', payload);
  },

  async uploadPhoto(file: File, userId?: string): Promise<User> {
    if (isDemoMode) {
      const updated = await demoAuth.uploadPhoto(file, userId);
      const { setCachedAvatar } = await import('../components/ui/PersonAvatar');
      setCachedAvatar(updated._id, updated.photoUrl ?? null);
      return updated;
    }
    const form = new FormData();
    form.append('file', file);
    const path = userId && userId !== 'me' ? `/users/${userId}/photo` : '/me/photo';
    const user = await api.upload<User>(path, form);
    const { setCachedAvatar } = await import('../components/ui/PersonAvatar');
    if (user._id) {
      // Clear cached blob or set new one so next fetch gets fresh photo
      setCachedAvatar(user._id, null);
    }
    return user;
  },

  async updateStudentProfile(userId: string | 'me', payload: Partial<StudentProfile>): Promise<StudentProfile> {
    if (isDemoMode) return demoAuth.updateStudentProfile(userId === 'me' ? demoAuth.getMe()._id : userId, payload);
    const path = userId === 'me' ? '/me/profile/student' : `/users/${userId}/profile/student`;
    return api.patch<StudentProfile>(path, payload);
  },

  async updateCoachProfile(userId: string | 'me', payload: Partial<CoachProfile>): Promise<CoachProfile> {
    if (isDemoMode) return demoAuth.updateCoachProfile(userId === 'me' ? demoAuth.getMe()._id : userId, payload);
    const path = userId === 'me' ? '/me/profile/coach' : `/users/${userId}/profile/coach`;
    return api.patch<CoachProfile>(path, payload);
  },

  logout(): void {
    if (isDemoMode) demoAuth.logout();
    else {
      localStorage.removeItem('accessToken');
      localStorage.removeItem('refreshToken');
    }
  },

  isAuthenticated(): boolean {
    if (isDemoMode) return demoAuth.isAuthenticated();
    return !!localStorage.getItem('accessToken');
  },
};
